import { Reducer } from 'redux'
import { CloudProvidersType } from '../../../admin/modules/NewCustomers/reducer'
import { CosellMappingManagerActionTypes } from './action'

export enum CoSellMappingManagerSectionName {
  COMPANY_AND_CONTACT = 'companyAndContact',
  HUBSPOT_DEAL_PIPELINE = 'hubspotDealPipeline',
  AWS_PARTNER_USER_DETAILS = 'awsPartnerUserDetails',
  UPDATE_REFERRAL_RECORD = 'updateReferralRecord',
  SYNC_REFERRAL_RECORD = 'syncReferralRecord',
  LEAD_AND_REFERRAL_SYNC = 'leadAndReferralSync',
  AUTO_CREATE_ACCOUNT_AND_OPPORTUNITY = 'autoCreateAccountAndOpportunity',
  UPDATE_REFERRAL_RECORD_SALESFORCE = 'updateReferralRecordSalesforce',
}

export interface CoSellCompanyAndContact {
  createCompany: boolean
  createContact: boolean
  createSenderCompany: boolean
  awsSalesRep: boolean
  awsAccountManager: boolean
  awsIsvSuccessManager: boolean
  awsPartnerSuccessManager: boolean
  partnerDevelopmentManager: boolean
  wwpsPartnerDevelopmentManager: boolean
  customerEndUserContactDetails: boolean
  senderContact: boolean
}

export interface ReferralPipeline {
  pipelineId: string
  pipelineDealStageId: string
}

export interface CoSellHubspotDealPipeline {
  autoAcceptIncomingReferral: boolean
  opportunityReferralPipeline: ReferralPipeline
  autoAcceptIncomingLead: boolean
  leadReferralPipeline: ReferralPipeline
}

export interface AwsPartnerUserDetails {
  id: string
  email: string
  lastName: string
  firstName: string
}

export interface CoSellAwsPartnerUserDetails {
  userDetails: AwsPartnerUserDetails[]
}

export interface StagesMapping {
  prospect: string[]
  qualified: string[]
  technicalValidation: string[]
  businessValidation: string[]
  committed: string[]
  launched: string[]
  closedLost: string[]
}

export interface CoSellUpdateReferralRecord {
  stage: boolean
  dealOwner: boolean
  nextSteps: boolean
  targetCloseDate: boolean
  stagesMapping: StagesMapping
}

export interface CoSellSyncReferralRecord {
  syncCustomHubspotFieldsForReporting: boolean
}
export interface CoSellLeadAndReferralSync {
  labraReferralSync: boolean
  partnerOriginatedReferralSync: boolean
  awsOriginatedReferralSync: boolean
  labraLeadSync: boolean
  autoAcceptIncomingReferral: boolean
}
export interface CoSellAutoCreateAccountAndOpportunity {
  autoCreateAccount: boolean
  autoCreateOpportunity: boolean
  syncReferralDataToOpportunity: boolean
  autoCreateMappings?: Record<string, string>
}

export interface CoSellUpdateReferralRecordSalesforce {
  stage: boolean
  targetCloseDate: boolean
  stagesMapping: StagesMapping
}
export type CosellMappingManagerSections = {
  [CoSellMappingManagerSectionName.COMPANY_AND_CONTACT]: CoSellCompanyAndContact
  [CoSellMappingManagerSectionName.HUBSPOT_DEAL_PIPELINE]: CoSellHubspotDealPipeline
  [CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS]: CoSellAwsPartnerUserDetails
  [CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD]: CoSellUpdateReferralRecord
  [CoSellMappingManagerSectionName.SYNC_REFERRAL_RECORD]: CoSellSyncReferralRecord
}

export type CosellMappingManagerSectionsForSalesforce = {
  [CoSellMappingManagerSectionName.COMPANY_AND_CONTACT]: CoSellCompanyAndContact
  [CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS]: CoSellAwsPartnerUserDetails
  [CoSellMappingManagerSectionName.LEAD_AND_REFERRAL_SYNC]: CoSellLeadAndReferralSync
  [CoSellMappingManagerSectionName.AUTO_CREATE_ACCOUNT_AND_OPPORTUNITY]: CoSellAutoCreateAccountAndOpportunity
  [CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD_SALESFORCE]: CoSellUpdateReferralRecordSalesforce
}

export enum MappingManagerTemplateStatusType {
  'DRAFT' = 'DRAFT',
  'ACTIVE' = 'ACTIVE',
}

export interface CosellTemplatesDetailsData {
  templateName: string
  templateId: string
  status:
    | MappingManagerTemplateStatusType.DRAFT
    | MappingManagerTemplateStatusType.ACTIVE
  createdBy: string
  creationDate: string
  lastUpdatedAt: string
}

export interface CosellFieldMappingPicklistData {
  referralValue: string
  hubspotValue: string
  hubspotLabel: string
}

export interface CosellFieldMapping {
  referralField: string
  referralFieldType: string
  hubspotObject: string
  hubspotField: string
  hubspotFieldLabel: string
  hubspotFieldType: string
  isHidden: boolean
  isReadonly: boolean
  isMapped: boolean
  label: string
  required: boolean
  displayOrder: number
  defaultValue: string | string[]
  category: string
  picklist: CosellHubspotFieldsOptions[] | null
  picklistMappings: CosellFieldMappingPicklistData[] | null
  crmField?: string
  crmFieldLabel?: string
  crmFieldType?: string
}

export interface CosellTemplatesDataFieldMappings {
  companyName?: CosellFieldMapping
  website?: CosellFieldMapping
  country?: CosellFieldMapping
  state?: CosellFieldMapping
  city?: CosellFieldMapping
  address?: CosellFieldMapping
  postalCode?: CosellFieldMapping
  dataUniversalNumberSystem?: CosellFieldMapping
  industry?: CosellFieldMapping
  otherIndustry?: CosellFieldMapping
  classifiedNationalSecurityInformation?: CosellFieldMapping
  customerFirstName?: CosellFieldMapping
  customerLastName?: CosellFieldMapping
  customerEmail?: CosellFieldMapping
  customerTitle?: CosellFieldMapping
  customerPhone?: CosellFieldMapping
  partnerProjectTitle?: CosellFieldMapping
  targetCloseDate?: CosellFieldMapping
  estimatedAwsMonthlyRecurringRevenue?: CosellFieldMapping
  description?: CosellFieldMapping
  deliveryModel?: CosellFieldMapping
  useCase?: CosellFieldMapping
  opportunityType?: CosellFieldMapping
  opportunityOwnerEmail?: CosellFieldMapping
  partnerPrimaryNeedFromAws?: CosellFieldMapping
  salesActivities?: CosellFieldMapping
  isFromMarketingActivity?: CosellFieldMapping
  wasMdfUsed?: CosellFieldMapping
  marketingActivityChannel?: CosellFieldMapping
  marketingActivityUseCase?: CosellFieldMapping
  parentOpportunityId?: CosellFieldMapping
  nextSteps?: CosellFieldMapping
  solutionOffered?: CosellFieldMapping
  otherSolutionOffered?: CosellFieldMapping
  marketingCampaignCode?: CosellFieldMapping
  additionalComments?: CosellFieldMapping
  contractEffectiveDate?: CosellFieldMapping
  contractExpirationDate?: CosellFieldMapping
  currency?: CosellFieldMapping
  customerSoftwareValueTvc?: CosellFieldMapping
  procurementType?: CosellFieldMapping
  apnPrograms?: CosellFieldMapping
  awsProducts?: CosellFieldMapping
  customerAwsAccountId?: CosellFieldMapping
  competitiveTracking?: CosellFieldMapping
  competitiveTrackingOther?: CosellFieldMapping
  offerId?: CosellFieldMapping
  partnerSalesContactEmail?: CosellFieldMapping
  partnerSalesContactFirstName?: CosellFieldMapping
  partnerSalesContactLastName?: CosellFieldMapping
  partnerSalesContactPrimaryPhone?: CosellFieldMapping
  partnerContactTitle?: CosellFieldMapping
}

export interface CosellSyncReferralMapping {
  label: string
  referralFieldType: string
  referralField: string
  hubspotField: string
  hubspotFieldType: string
  hubspotObject: string
  hubspotFieldLabel: string
  displayOrder: number
  syncToHubspot: boolean
}

export interface CosellReferralDataFieldMappings {
  companyName?: CosellSyncReferralMapping
  website?: CosellSyncReferralMapping
  country?: CosellSyncReferralMapping
  state?: CosellSyncReferralMapping
  city?: CosellSyncReferralMapping
  address?: CosellSyncReferralMapping
  postalCode?: CosellSyncReferralMapping
  dataUniversalNumberSystem?: CosellSyncReferralMapping
  industry?: CosellSyncReferralMapping
  otherIndustry?: CosellSyncReferralMapping
  classifiedNationalSecurityInformation?: CosellSyncReferralMapping
  customerFirstName?: CosellSyncReferralMapping
  customerLastName?: CosellSyncReferralMapping
  customerEmail?: CosellSyncReferralMapping
  customerTitle?: CosellSyncReferralMapping
  customerPhone?: CosellSyncReferralMapping
  partnerProjectTitle?: CosellSyncReferralMapping
  targetCloseDate?: CosellSyncReferralMapping
  estimatedAwsMonthlyRecurringRevenue?: CosellSyncReferralMapping
  description?: CosellSyncReferralMapping
  deliveryModel?: CosellSyncReferralMapping
  useCase?: CosellSyncReferralMapping
  opportunityType?: CosellSyncReferralMapping
  parentOpportunityId?: CosellSyncReferralMapping
  opportunityOwnerEmail?: CosellSyncReferralMapping
  partnerPrimaryNeedFromAws?: CosellSyncReferralMapping
  salesActivities?: CosellSyncReferralMapping
  isFromMarketingActivity?: CosellSyncReferralMapping
  wasMdfUsed?: CosellSyncReferralMapping
  marketingActivityChannel?: CosellSyncReferralMapping
  marketingActivityUseCase?: CosellSyncReferralMapping
  marketingCampaignCode?: CosellSyncReferralMapping
  nextSteps?: CosellSyncReferralMapping
  solutionOffered?: CosellSyncReferralMapping
  otherSolutionOffered?: CosellSyncReferralMapping
  referralStage?: CosellSyncReferralMapping
  referredBy?: CosellSyncReferralMapping
  additionalComments?: CosellSyncReferralMapping
  contractEffectiveDate?: CosellSyncReferralMapping
  contractExpirationDate?: CosellSyncReferralMapping
  currency?: CosellSyncReferralMapping
  customerSoftwareValueTvc?: CosellSyncReferralMapping
  procurementType?: CosellSyncReferralMapping
  apnPrograms?: CosellSyncReferralMapping
  awsProducts?: CosellSyncReferralMapping
  customerAwsAccountId?: CosellSyncReferralMapping
  competitiveTracking?: CosellSyncReferralMapping
  competitiveTrackingOther?: CosellSyncReferralMapping
  offerId?: CosellSyncReferralMapping
  awsMarketplaceEngagementScore?: CosellSyncReferralMapping
  awsInternalStage?: CosellSyncReferralMapping
  awsCloseDate?: CosellSyncReferralMapping
  awsClosedLostReason?: CosellSyncReferralMapping
  awsRecommendedAction?: CosellSyncReferralMapping
  customerSegment?: CosellSyncReferralMapping
  isGreenfield?: CosellSyncReferralMapping
  awsOpportunityId?: CosellSyncReferralMapping
  partnerAcceptanceStatus?: CosellSyncReferralMapping
  awsAcceptanceStatus?: CosellSyncReferralMapping
  awsSalesRepEmail?: CosellSyncReferralMapping
  awsSalesRepName?: CosellSyncReferralMapping
  awsAccountOwnerName?: CosellSyncReferralMapping
  awsAccountOwnerEmail?: CosellSyncReferralMapping
  awsIsvSuccessManagerName?: CosellSyncReferralMapping
  awsIsvSuccessManagerEmail?: CosellSyncReferralMapping
  awsPartnerSuccessManagerName?: CosellSyncReferralMapping
  awsPartnerSuccessManagerEmail?: CosellSyncReferralMapping
  partnerDevelopmentManagerName?: CosellSyncReferralMapping
  partnerDevelopmentManagerEmail?: CosellSyncReferralMapping
  otherPartnerDevelopmentManagerName?: CosellSyncReferralMapping
  otherPartnerDevelopmentManagerEmail?: CosellSyncReferralMapping
  partnerSalesContactEmail?: CosellSyncReferralMapping
  partnerSalesContactFirstName?: CosellSyncReferralMapping
  partnerSalesContactLastName?: CosellSyncReferralMapping
  partnerSalesContactPrimaryPhone?: CosellSyncReferralMapping
  partnerContactTitle?: CosellSyncReferralMapping
}

export interface CosellTemplateDataResponse {
  resetToDefault?: boolean
  id: string
  name: string
  templateType: string
  autoReferralCreation: boolean
  autoReferralDealtypeTrigger?: string
  cloudProvider: CloudProvidersType
  isDraft: boolean
  isActive?: boolean
  fieldMappings: CosellTemplatesDataFieldMappings
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
  crmId?: string
  accountId?: string
  productType?: string
  crmType?: string
  autoReferralTriggerCriteria?: Record<string, string> | null
}

export interface CosellSalesforceTemplateItem {
  id: string
  name: string
  crmId: string
  accountId: string
  templateType: string
  productType: string
  cloudProvider: string
  crmType: string
  isDraft: boolean
  autoReferralCreation: boolean
  createdBy: string
  createdAt: string
  updatedAt: string | null
}

export interface CosellTemplateDataResponseSalesforce {
  mappingTemplates: CosellSalesforceTemplateItem[]
  totalCount: number
  pageNumber: number
  pageSize: number
  sortBy: string
  sortOrder: 'ASC' | 'DESC'
}

export interface CosellTemplateSectionData {
  customerDetails: Partial<CosellTemplatesDataFieldMappings>
  referralDetails: Partial<CosellTemplatesDataFieldMappings>
  awsDetails: Partial<CosellTemplatesDataFieldMappings>
  contacts: Partial<CosellTemplatesDataFieldMappings>
  dealDetails: Partial<CosellTemplatesDataFieldMappings>
  solutionsOffered: Partial<CosellTemplatesDataFieldMappings>
  helpFromMicrosoft: Partial<CosellTemplatesDataFieldMappings>
}

export interface CosellReferralMappingSectionData {
  customerDetails: Partial<CosellReferralDataFieldMappings>
  referralDetails: Partial<CosellReferralDataFieldMappings>
  awsDetails: Partial<CosellReferralDataFieldMappings>
  contacts: Partial<CosellReferralDataFieldMappings>
}

export interface CosellTemplateData {
  templateData: CosellTemplateDataResponse
  sectionData: CosellTemplateSectionData
}

export interface CosellHubspotFieldsOptions {
  value: string
  label: string
  options?: CosellHubspotFieldsOptions[]
}

export interface CosellHubspotFieldsData {
  internalName: string
  displayLabel: string
  fieldType: string
  objectType: string
  description: string
  options?: CosellHubspotFieldsOptions[]
}
export interface StandardValue {
  label: string
  value: string
}
export interface FieldDefinition {
  label: string
  name: string
  type: string
  isPrimaryKey: boolean
  isMandatory: boolean
  mappedDataType: string
  maxLength: number
  standardValues: StandardValue[] | null
}
export interface CosellSalesforceFieldsData {
  Opportunity: FieldDefinition[]
  Account: FieldDefinition[]
}

export interface CosellHubspotFieldsDataResponse {
  fields: CosellHubspotFieldsData[]
}

export interface CosellTemplateReferralMapping {
  referralMappings: CosellReferralDataFieldMappings
  sectionData: CosellReferralMappingSectionData
}

export interface CoSellReferralMapping {
  fieldMappings: CosellReferralDataFieldMappings
}
export interface CoSellMappingManagerData {
  // Salesforce-specific metadata fields (optional, only populated for Salesforce)
  id: string | null
  crmId: string | null
  accountId: string | null
  cloudType: string | null
  crmType: string | null
  updatedBy: string | null
  createdAt: string | null
  updatedAt: string | null
  // Section data can be either HubSpot or Salesforce structure
  sectionData:
    | CosellMappingManagerSections
    | CosellMappingManagerSectionsForSalesforce
  originalCrmSchema: any | null
  templateDetails: CosellTemplatesDetailsData[]
  templateData: CosellTemplateData
  crmFields: CosellHubspotFieldsDataResponse
  salesforceFields?: CosellSalesforceFieldsData
  labraFields: CosellHubspotFieldsDataResponse
  referralMapping: CosellTemplateReferralMapping
}
export interface CoSellMappingManagerDataForSalesforce {
  id: string | null
  crmId: string | null
  accountId: string | null
  cloudType: string | null
  crmType: string | null
  updatedBy: string | null
  createdAt: string | null
  updatedAt: string | null
  sectionData: CosellMappingManagerSectionsForSalesforce
  originalCrmSchema: any | null
  templateDetails: CosellTemplatesDetailsData[]
  templateData: CosellTemplateData
  crmFields: CosellHubspotFieldsDataResponse
  labraFields: CosellHubspotFieldsDataResponse
  referralMapping: CosellTemplateReferralMapping
}

export interface CosellSalesforceTemplatesState {
  data: CosellTemplateDataResponseSalesforce | null
}

const initialSalesforceTemplatesState: CosellSalesforceTemplatesState = {
  data: null,
}

export enum CosellSalesforceTemplatesActionTypes {
  SET_SALESFORCE_REFERRAL_TEMPLATES = 'SET_SALESFORCE_REFERRAL_TEMPLATES',
  CLEAR_SALESFORCE_REFERRAL_TEMPLATES = 'CLEAR_SALESFORCE_REFERRAL_TEMPLATES',
}

export type CosellSalesforceTemplatesAction =
  | {
      type: CosellSalesforceTemplatesActionTypes.SET_SALESFORCE_REFERRAL_TEMPLATES
      payload: CosellTemplateDataResponseSalesforce
    }
  | {
      type: CosellSalesforceTemplatesActionTypes.CLEAR_SALESFORCE_REFERRAL_TEMPLATES
    }

export const cosellSalesforceTemplatesReducer: Reducer<
  CosellSalesforceTemplatesState,
  CosellSalesforceTemplatesAction
> = (state = initialSalesforceTemplatesState, action) => {
  switch (action.type) {
    case CosellSalesforceTemplatesActionTypes.SET_SALESFORCE_REFERRAL_TEMPLATES: {
      return {
        ...state,
        data: action.payload,
      }
    }
    case CosellSalesforceTemplatesActionTypes.CLEAR_SALESFORCE_REFERRAL_TEMPLATES: {
      return initialSalesforceTemplatesState
    }
    default: {
      return state
    }
  }
}

const initialState: CoSellMappingManagerData = {
  // Salesforce metadata fields (null for HubSpot)
  id: null,
  crmId: null,
  accountId: null,
  cloudType: null,
  crmType: null,
  updatedBy: null,
  createdAt: null,
  updatedAt: null,
  sectionData: {
    [CoSellMappingManagerSectionName.COMPANY_AND_CONTACT]: {
      createCompany: false,
      createContact: false,
      createSenderCompany: false,
      awsSalesRep: false,
      awsAccountManager: false,
      awsIsvSuccessManager: false,
      awsPartnerSuccessManager: false,
      partnerDevelopmentManager: false,
      wwpsPartnerDevelopmentManager: false,
      customerEndUserContactDetails: false,
      senderContact: false,
    },
    [CoSellMappingManagerSectionName.HUBSPOT_DEAL_PIPELINE]: {
      autoAcceptIncomingReferral: false,
      opportunityReferralPipeline: {
        pipelineId: '',
        pipelineDealStageId: '',
      },
      autoAcceptIncomingLead: false,
      leadReferralPipeline: {
        pipelineId: '',
        pipelineDealStageId: '',
      },
    },
    [CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS]: {
      userDetails: [
        {
          id: `${Math.floor(Math.random() * 10000)}`,
          firstName: '',
          lastName: '',
          email: '',
        },
      ],
    },
    [CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD]: {
      stage: false,
      dealOwner: false,
      nextSteps: false,
      targetCloseDate: false,
      stagesMapping: {
        prospect: [],
        qualified: [],
        technicalValidation: [],
        businessValidation: [],
        committed: [],
        launched: [],
        closedLost: [],
      },
    },
    [CoSellMappingManagerSectionName.SYNC_REFERRAL_RECORD]: {
      syncCustomHubspotFieldsForReporting: false,
    },
  },
  originalCrmSchema: null,
  templateDetails: [] as CosellTemplatesDetailsData[],
  templateData: {} as CosellTemplateData,
  crmFields: {} as CosellHubspotFieldsDataResponse,
  salesforceFields: {} as CosellSalesforceFieldsData,
  labraFields: {} as CosellHubspotFieldsDataResponse,
  referralMapping: {} as CosellTemplateReferralMapping,
}
export const initialStateForSalesforce: CoSellMappingManagerDataForSalesforce =
  {
    id: null,
    crmId: null,
    accountId: null,
    cloudType: null,
    crmType: null,
    updatedBy: null,
    createdAt: null,
    updatedAt: null,
    sectionData: {
      [CoSellMappingManagerSectionName.COMPANY_AND_CONTACT]: {
        createCompany: false,
        createContact: false,
        createSenderCompany: false,
        awsSalesRep: false,
        awsAccountManager: false,
        awsIsvSuccessManager: false,
        awsPartnerSuccessManager: false,
        partnerDevelopmentManager: false,
        wwpsPartnerDevelopmentManager: false,
        customerEndUserContactDetails: false,
        senderContact: false,
      },
      [CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS]: {
        userDetails: [
          {
            id: `${Math.floor(Math.random() * 10000)}`,
            firstName: '',
            lastName: '',
            email: '',
          },
        ],
      },
      [CoSellMappingManagerSectionName.LEAD_AND_REFERRAL_SYNC]: {
        labraReferralSync: true,
        partnerOriginatedReferralSync: true,
        awsOriginatedReferralSync: true,
        labraLeadSync: false,
        autoAcceptIncomingReferral: false,
      },
      [CoSellMappingManagerSectionName.AUTO_CREATE_ACCOUNT_AND_OPPORTUNITY]: {
        autoCreateAccount: false,
        autoCreateOpportunity: false,
        syncReferralDataToOpportunity: false,
        autoCreateMappings: {},
      },
      [CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD_SALESFORCE]: {
        stage: false,
        targetCloseDate: false,
        stagesMapping: {
          prospect: [],
          qualified: [],
          technicalValidation: [],
          businessValidation: [],
          committed: [],
          launched: [],
          closedLost: [],
        },
      },
    },
    originalCrmSchema: null,
    templateDetails: [] as CosellTemplatesDetailsData[],
    templateData: {} as CosellTemplateData,
    crmFields: {} as CosellHubspotFieldsDataResponse,
    labraFields: {} as CosellHubspotFieldsDataResponse,
    referralMapping: {} as CosellTemplateReferralMapping,
  }

type actionType =
  | {
      type: CosellMappingManagerActionTypes.SET_TEMPLATE_DATA
      payload: CosellTemplateDataResponse
    }
  | {
      type: CosellMappingManagerActionTypes.SET_REFERRAL_MAPPING_DATA
      payload: CosellReferralDataFieldMappings
    }
  | {
      type: CosellMappingManagerActionTypes.SET_TEMPLATE_DATA_INFO
      payload: CosellTemplatesDetailsData[]
    }
  | {
      type: CosellMappingManagerActionTypes.SET_HUBSPOT_FIELDS
      payload: CosellHubspotFieldsData[]
    }
  | {
      type: CosellMappingManagerActionTypes.SET_SECTION_WISE_FIELDS
      payload: CosellTemplateSectionData
    }
  | {
      type: CosellMappingManagerActionTypes.SET_SECTION_WISE_RFERRAL_MAPPING
      payload: CosellReferralMappingSectionData
    }
  | {
      type: CosellMappingManagerActionTypes.SET_CO_SELL_MAPPING_SECTION_DATA
      payload:
        | CosellMappingManagerSections
        | CosellMappingManagerSectionsForSalesforce
    }
  | {
      type: CosellMappingManagerActionTypes.SET_ORIGINAL_CRM_SCHEMA
      payload: any
    }
  | {
      type: CosellMappingManagerActionTypes.UPDATE_TEMPLATE_SECTION_FIELD
      payload: {
        section: keyof CosellTemplateSectionData
        fieldKey: keyof CosellTemplatesDataFieldMappings
        updates: Partial<CosellFieldMapping>
      }
    }
  | {
      type: CosellMappingManagerActionTypes.UPDATE_MAPPING_SECTION_FILED
      payload: {
        section: keyof CosellReferralMappingSectionData
        fieldKey: keyof CosellReferralDataFieldMappings
        updates: Partial<CosellSyncReferralMapping>
      }
    }
  | {
      type: CosellMappingManagerActionTypes.SET_TEMPLATE_AS_DRAFT
      payload: boolean
    }
  | {
      type: CosellMappingManagerActionTypes.SET_AUTOMATE_REFERRAL_CREATION
      payload: boolean
    }
  | {
      type: CosellMappingManagerActionTypes.SET_AUTOMATE_REFERRAL_CREATION_TRIGGER
      payload: string
    }
  | {
      type: CosellMappingManagerActionTypes.SET_SYNC_CONFIGURATIONS
      payload: any
    }
  | {
      type: CosellMappingManagerActionTypes.SET_SALESFORCE_FIELDS
      payload: CosellSalesforceFieldsData
    }
  | {
      type: CosellMappingManagerActionTypes.SET_LABRA_FIELDS
      payload: CosellHubspotFieldsData[]
    }

export const reducer: Reducer<CoSellMappingManagerData, actionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case CosellMappingManagerActionTypes.SET_TEMPLATE_DATA: {
      return {
        ...state,
        templateData: {
          ...state.templateData,
          templateData: action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_REFERRAL_MAPPING_DATA: {
      return {
        ...state,
        referralMapping: {
          ...state.referralMapping,
          referralMappings: action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_TEMPLATE_DATA_INFO: {
      return {
        ...state,
        templateDetails: action.payload,
      }
    }
    case CosellMappingManagerActionTypes.SET_TEMPLATE_AS_DRAFT: {
      return {
        ...state,
        templateData: {
          ...state.templateData,
          templateData: {
            ...state.templateData.templateData,
            isDraft: action.payload,
            isActive: !action.payload,
          },
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_HUBSPOT_FIELDS: {
      return {
        ...state,
        crmFields: {
          fields: action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_LABRA_FIELDS: {
      return {
        ...state,
        labraFields: {
          fields: action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_SECTION_WISE_FIELDS: {
      return {
        ...state,
        templateData: {
          ...state.templateData,
          sectionData: action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_SECTION_WISE_RFERRAL_MAPPING: {
      return {
        ...state,
        referralMapping: {
          ...state.referralMapping,
          sectionData: action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_AUTOMATE_REFERRAL_CREATION: {
      return {
        ...state,
        templateData: {
          ...state.templateData,
          templateData: {
            ...state.templateData.templateData,
            autoReferralCreation: action.payload,
          },
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_AUTOMATE_REFERRAL_CREATION_TRIGGER: {
      return {
        ...state,
        templateData: {
          ...state.templateData,
          templateData: {
            ...state.templateData.templateData,
            autoReferralDealtypeTrigger: action.payload,
          },
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_CO_SELL_MAPPING_SECTION_DATA: {
      return {
        ...state,
        sectionData: {
          ...state.sectionData,
          ...action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_SYNC_CONFIGURATIONS: {
      return {
        ...state,
        id: action.payload?.id ?? null,
        crmId: action.payload?.crmId ?? null,
        accountId: action.payload?.accountId ?? null,
        cloudType: action.payload?.cloudType ?? null,
        crmType: action.payload?.crmType ?? null,
        updatedBy: action.payload?.updatedBy ?? null,
        createdAt: action.payload?.createdAt ?? null,
        updatedAt: action.payload?.updatedAt ?? null,
      }
    }
    case CosellMappingManagerActionTypes.SET_ORIGINAL_CRM_SCHEMA: {
      return {
        ...state,
        originalCrmSchema: {
          ...state.originalCrmSchema,
          ...action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.UPDATE_TEMPLATE_SECTION_FIELD: {
      const { section, fieldKey, updates } = action.payload
      return {
        ...state,
        templateData: {
          ...state.templateData,
          sectionData: {
            ...state.templateData.sectionData,
            [section]: {
              ...(state.templateData.sectionData?.[section] ?? {}),
              [fieldKey]: {
                ...(state.templateData.sectionData?.[section]?.[fieldKey] ??
                  {}),
                ...updates,
              },
            },
          },
        },
      }
    }
    case CosellMappingManagerActionTypes.UPDATE_MAPPING_SECTION_FILED: {
      const { section, fieldKey, updates } = action.payload
      return {
        ...state,
        referralMapping: {
          ...state.referralMapping,
          sectionData: {
            ...state.referralMapping.sectionData,
            [section]: {
              ...(state.referralMapping.sectionData?.[section] ?? {}),
              [fieldKey]: {
                ...(state.referralMapping.sectionData?.[section]?.[fieldKey] ??
                  {}),
                ...updates,
              },
            },
          },
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_SALESFORCE_FIELDS: {
      return {
        ...state,
        salesforceFields: action.payload,
      }
    }
    default: {
      return state
    }
  }
}

export const CoSellMappingManagerDataForSalesforce: Reducer<
  CoSellMappingManagerDataForSalesforce,
  actionType
> = (state = initialStateForSalesforce, action) => {
  switch (action.type) {
    case CosellMappingManagerActionTypes.SET_SYNC_CONFIGURATIONS: {
      return {
        ...state,
        id: action.payload?.id ?? null,
        crmId: action.payload?.crmId ?? null,
        accountId: action.payload?.accountId ?? null,
        cloudType: action.payload?.cloudType ?? null,
        crmType: action.payload?.crmType ?? null,
        updatedBy: action.payload?.updatedBy ?? null,
        createdAt: action.payload?.createdAt ?? null,
        updatedAt: action.payload?.updatedAt ?? null,
      }
    }
    case CosellMappingManagerActionTypes.SET_TEMPLATE_DATA_INFO: {
      return {
        ...state,
        templateDetails: action.payload,
      }
    }
    case CosellMappingManagerActionTypes.SET_CO_SELL_MAPPING_SECTION_DATA: {
      return {
        ...state,
        sectionData: {
          ...state.sectionData,
          ...(action.payload as Partial<CosellMappingManagerSectionsForSalesforce>),
        },
      }
    }
    default: {
      return state
    }
  }
}
